import {Workspace} from '../../API';
import {DataSource, FilterSchema} from "./DataSource";

import {listWorkspaces} from "./requests/listWorkspaces";
import {getColor} from "../../utils/stringToColor";
import {syncWorkspaceUpdate} from "../../sync/workspaceSync";
import {selectWorkspaceFromPlain} from "../../actions/ui/selectWorkspace";

import {onCreateWorkspace, onDeleteWorkspace, onUpdateWorkspace} from "../../graphql/subscriptions";
import { FullDataSourceDeps } from "../../platform/services/types/DataSourceDependencies";

export class WorkspacesDataSource extends DataSource<Workspace, Workspace, FullDataSourceDeps> {
    readonly _modelName = 'Workspace' as const;
    readonly _subscriptionGQLs = {
        onCreate: onCreateWorkspace,
        onUpdate: onUpdateWorkspace,
        onDelete: onDeleteWorkspace,
    } as const;

    constructor(deps: FullDataSourceDeps) {
        super(deps);
        this.setSortRule('createdAt');
    }

    get syncHash() {
        return '';
    }

    getWorkspaceById(wsId: string) {
        return this.allRows.find(wsRow => wsRow.id === wsId);
    }

    getFilterDefaultValues(): {} {
        return {}
    }

    getFilterSchema(): FilterSchema<Workspace> {
        return {
            name: {
                type: 'client',
                apply: (workspace: Workspace) => (
                    this.filterValues.name === undefined
                    || workspace.name?.toLowerCase().indexOf(this.filterValues.name.toLowerCase()) !== -1
                ),
            },
        }
    }

    getWorkspaceColor = (wsId?: string, alpha?: number) => {
        if (!wsId) {
            return 'gray';
        }
        const workspace = this.getWorkspaceById(wsId);
        if (!workspace) {
            return 'gray';
        }
        const workspaceColor = getColor(workspace);
        if (alpha) {
            return `hsl(${workspaceColor.hue}, ${workspaceColor.saturation}%, ${workspaceColor.lightness}%, ${alpha})`;
        } else {
            return `hsl(${workspaceColor.hue}, ${workspaceColor.saturation}%, ${workspaceColor.lightness}%)`;
        }
    }

    loadGQLFetch() {
        return listWorkspaces(
            this.deps.dataClientService,
            this.deps.authService
        );
    };

    loadMoreGQLFetch(nextToken: string) {
        return listWorkspaces(
            this.deps.dataClientService,
            this.deps.authService,
            nextToken
        );
    };

    getWsClient() {
        return this.deps.dataClientService.getWsClientForTable('workspaces');
    };

    onItemUpdate = (serverWs: Workspace) => {
        const currentWsMST = this.deps.rootStore.view.workspace;
        if (serverWs.id === currentWsMST?.id) {
            syncWorkspaceUpdate(currentWsMST, serverWs);
        }
    }

    onItemDelete = (serverWs: Workspace) => {
        const currentWsMST = this.deps.rootStore.view.workspace;
        if (serverWs.id === currentWsMST?.id) {
            const availableWorkspaces = this.deps.userState.availableWorkspaces;
            if (availableWorkspaces.length > 0) {
                selectWorkspaceFromPlain(this.deps.rootStore, this.deps.userState.dsManager!, availableWorkspaces[0]);
            } else {
                this.deps.rootStore.view.setWorkspace(null);
            }
        }
    };
}
